import { isDefined } from "@clipboard-health/util-ts";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { type QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";

import { IN_HOME_USER_EVENTS } from "../lib/events";

interface UseLogOpenVisitViewedProps {
  workplaceId: string;
  visitId: string;
  visitType: string;
  workerReq?: string;
  payRate?: number;
  pricingType: string;
  distanceInMiles?: number;
}

function getVisitViewedLogCacheKey(visitId: string) {
  return ["visitViewedLog", visitId];
}

function cacheVisitViewedLog(queryClient: QueryClient, visitId: string) {
  queryClient.setQueryData(getVisitViewedLogCacheKey(visitId), () => true);
}

function getVisitViewedLogCache(queryClient: QueryClient, visitId: string) {
  if (!isDefined(visitId)) {
    return false;
  }

  return queryClient.getQueryData<boolean>(getVisitViewedLogCacheKey(visitId));
}

export function useLogOpenVisitViewed(props: UseLogOpenVisitViewedProps) {
  const queryClient = useQueryClient();

  const { visitId } = props;

  const propsRef = useRef(props);

  const { mutate: logOpenVisitViewed } = useMutation({
    mutationFn: async (params: UseLogOpenVisitViewedProps) => {
      const { workplaceId, visitId, visitType, workerReq, payRate, pricingType, distanceInMiles } =
        params;

      if (!isDefined(visitId)) {
        return;
      }

      if (getVisitViewedLogCache(queryClient, visitId)) {
        // Prevent duplicate logs for the same visit in the same session
        return;
      }

      logEvent(IN_HOME_USER_EVENTS.VISIT_VIEWED, {
        workplaceId,
        visitId,
        visitType,
        workerReq,
        payRate,
        pricingType,
        distanceInMiles,
      });
    },
    onSuccess: (_, params) => {
      // Cache the log to prevent duplication
      cacheVisitViewedLog(queryClient, params.visitId);
    },
  });

  const { ref, inView } = useInView({
    rootMargin: "0px",
    threshold: 0.2,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      logOpenVisitViewed(propsRef.current);
    }
  }, [inView, visitId, logOpenVisitViewed]);

  return { ref, inView };
}
